import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    FIELD_LABELS,
    formatDate,
    formatFloat,
    shortLabel,
    useNumber,
} from '../../../../../../helpers';
import {
  OrderProductionsPageQuery,
  useOrderProductionsPageLazyQuery
} from '../../../../../../services';
import {
  CCardList,
  CMinifiedTable,
  DateRange,
  DBlock,
  DDateRangePicker,
  DIdAutocomplete,
  ListScope,
  selectPaginated,
  usePaginatedListBlock
} from '../../../../../dum';
import {
  gradeLine,
  HOURLY_DATA_EPOCH,
  PerformanceFlagChips,
  ProductionGrade,
  SDeleteOrderProductionDialog,
  summarizeGrades,
  SUpsertOrderProductionDialog,
  useRatesCache
} from '../../../../../smart';
import { OrderProductionCard } from './order-production-card';
import {
    OrderProductionPerformanceDialog,
    PerformanceDialogLine,
} from '../../../production-performance/order-production-performance-dialog';

type ProductionDoc =
    OrderProductionsPageQuery['paginatedOrderProductions']['docs'][number];

interface IOrderProductionsListBlock {
    // When set, embeds the block on a parent's page: a product (all productions
    // of that product) or a machine (all productions on that machine — reused by
    // a future machine page). Undefined renders the full standalone Producción page.
    scope?: ListScope;
    // Isolates filter/paginator localStorage state. Pass a unique key when
    // embedding (e.g. on the product page) so state doesn't collide with the
    // standalone Producción page.
    storageKey?: string;
}

export function OrderProductionsListBlock({
    scope,
    storageKey,
}: IOrderProductionsListBlock = {}) {
    const KEY = storageKey ?? OrderProductionsListBlock.name;
    const embedded = !!scope;
    const productId = scope?.type === 'product' ? scope.id : null;
    const machineScopeId = scope?.type === 'machine' ? scope.id : null;
    const [query, { data, previousData, loading }] =
        useOrderProductionsPageLazyQuery();

    const [orderProductionTypeId, setOrderProductionTypeId] = useNumber({
        KEY: KEY + '_OrderProductionTypeId',
    });
    const [branchId, setBranchId] = useNumber({ KEY: KEY + '_BranchId' });
    const [machineId, setMachineId] = useNumber({ KEY: KEY + '_MachineId' });

    const {
        filter,
        setFilter,
        debouncedFilter,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        skip,
        take,
        setSkip,
        selectedId: orderProductionId,
        upsertOpen,
        setUpsertOpen,
        deleteOpen,
        setDeleteOpen,
        openEdit,
        openDelete,
    } = usePaginatedListBlock({
        storageKey: KEY,
        embedded,
        resetDeps: [branchId, machineId, orderProductionTypeId],
    });

    useEffect(() => {
        void query({
            variables: {
                skip: skip,
                take: take,
                startDate: startDate || null,
                endDate: endDate || null,
                orderProductionTypeId: orderProductionTypeId,
                branchId: branchId,
                // A machine scope (machine page) takes precedence over the
                // in-table machine filter; otherwise use the column filter.
                machineId: machineScopeId ?? machineId,
                productId: productId,
                filter: debouncedFilter || null,
            },
        });
    }, [query, skip, take, startDate, endDate, orderProductionTypeId, branchId, machineId, machineScopeId, productId, debouncedFilter]);

    const handleEditOrderProduction = useCallback(
        (op: ProductionDoc) => openEdit(op.id),
        [openEdit],
    );
    const handleDeleteOrderProduction = useCallback(
        (op: ProductionDoc) => openDelete(op.id),
        [openDelete],
    );

    // One performance ("grade") dialog for the whole list; the clicked card
    // fills these — same pattern as the planning board's status dialog.
    const [gradeOpen, setGradeOpen] = useState(false);
    const [gradeProductionId, setGradeProductionId] = useState<number | null>(
        null,
    );
    const [gradeLines, setGradeLines] = useState<PerformanceDialogLine[]>([]);
    const handleGradeOrderProduction = useCallback((op: ProductionDoc) => {
        // Dedupe by product×machine — a production can repeat the same combo
        // across lines, but the analysis is per combo.
        const byCombo = new Map<string, PerformanceDialogLine>();
        op.order_production_products.forEach((opp) => {
            if (!opp.product?.id || !opp.machine?.id) return;
            byCombo.set(`${opp.product.id}-${opp.machine.id}`, {
                machineId: opp.machine.id,
                machineName: opp.machine.name || '',
                productId: opp.product.id,
                productDescription: opp.product.compound_description || '',
            });
        });
        setGradeProductionId(op.id);
        setGradeLines(Array.from(byCombo.values()));
        setGradeOpen(true);
    }, []);

    const { docs, count } = selectPaginated(
        data?.paginatedOrderProductions,
        previousData?.paginatedOrderProductions,
    );

    // Primitives in deps so a fresh `scope` object from the parent doesn't bust
    // the memos below.
    const scopeType = scope?.type;
    const scopeId = scope?.id;

    // ---- performance flags -------------------------------------------------
    // Every product x machine combo on the visible page, plus each machine's
    // fallback rate, resolved in ONE batched request (and cache-first, so
    // paging back to a seen page costs nothing). Deliberately not stored on the
    // record: the flag compares a run to a baseline that every later run moves,
    // so a value frozen at save time would answer a question nobody asked.
    const { ensure, get } = useRatesCache();

    useEffect(() => {
        const pairs = docs.flatMap((op) =>
            op.order_production_products.flatMap((opp) => {
                const machineId = opp.machine?.id;
                const productId = opp.product?.id;
                if (!machineId || !productId) return [];
                return [
                    { machineId, productId },
                    // Fallback for a combo with no history of its own.
                    { machineId, productId: null },
                ];
            }),
        );
        if (pairs.length > 0) ensure(pairs);
    }, [docs, ensure]);

    const grades = useMemo(() => {
        const byProduction = new Map<number, ProductionGrade>();
        docs.forEach((op) => {
            // The production's waste belongs to the run, not to any one line;
            // a line owns its kilo share of it. Same proration the Rendimiento
            // page uses, so the two surfaces cannot disagree.
            const totalKilos = op.order_production_products.reduce(
                (acc, opp) => acc + opp.kilos,
                0,
            );
            // formatDate is the UTC calendar-date reader and emits DATE_FORMAT,
            // which is exactly the basis the SQL windows compare against.
            const startDate = formatDate(op.start_date);

            const lines = op.order_production_products.flatMap((opp) => {
                const machineId = opp.machine?.id;
                const productId = opp.product?.id;
                if (!machineId || !productId) return [];
                // On a product or machine page the row represents only that
                // parent's share of the run, so grading the other lines would
                // flag a row for something it isn't showing.
                if (scopeType === 'product' && productId !== scopeId) return [];
                if (scopeType === 'machine' && machineId !== scopeId) return [];
                return [
                    gradeLine({
                        machineId,
                        productId,
                        startDate,
                        kilos: opp.kilos,
                        groups: opp.groups,
                        hours: opp.hours ?? null,
                        wasteShare:
                            totalKilos > 0
                                ? op.waste * (opp.kilos / totalKilos)
                                : 0,
                        rate: get(machineId, productId),
                        machineRate: get(machineId, null),
                        // The list reads the default epoch window (get/ensure
                        // default to it too), so grade against the same bound.
                        windowFrom: HOURLY_DATA_EPOCH,
                    }),
                ];
            });

            byProduction.set(op.id, summarizeGrades(lines));
        });
        return byProduction;
    }, [docs, get, scopeType, scopeId]);

    // Sum each row's scope-relevant lines once (memoized) for the embedded
    // columns. Product scope keeps the lines of that product (and lists the
    // machines that ran it); machine scope keeps the lines of that machine (and
    // lists the products it produced).
    const summaries = useMemo(() => {
        if (!scopeType || scopeId == null) return [];
        return docs.map((op) => {
            const lines = op.order_production_products.filter((p) =>
                scopeType === 'product'
                    ? p.product?.id === scopeId
                    : p.machine?.id === scopeId,
            );
            return {
                kilos: lines.reduce((a, p) => a + p.kilos, 0),
                groups: lines.reduce((a, p) => a + p.groups, 0),
                detail: Array.from(
                    new Set(
                        lines
                            .map((p) =>
                                scopeType === 'product'
                                    ? p.machine?.name
                                    : p.product?.compound_description,
                            )
                            .filter(Boolean),
                    ),
                ).join(', '),
            };
        });
    }, [docs, scopeType, scopeId]);

    if (scope) {
        return (
            <>
                <CMinifiedTable
                    title={'Producción'}
                    loading={loading}
                    filterValue={filter}
                    onFilterChange={(value) => setFilter(value || '')}
                    filterHelperText={'Tipo, planta, máquina, producto o empleado.'}
                    skip={skip}
                    take={take}
                    setSkip={setSkip}
                    count={count}
                    onRowDoubleClick={(op) => handleEditOrderProduction(op)}
                    items={[
                        { title: shortLabel(FIELD_LABELS.start_date), textAlign: 'center', content: (op) => formatDate(op.start_date) },
                        { title: shortLabel(FIELD_LABELS.branch_id), content: (op) => op.branch?.name },
                        { title: shortLabel(scope.type === 'machine' ? COLUMN_LABELS.products_list : COLUMN_LABELS.machines_list), content: (_op, i) => summaries[i]?.detail },
                        {
                            title: shortLabel(COLUMN_LABELS.employees_list),
                            content: (op) =>
                                op.order_production_employees
                                    .map((ope) => ope.employee?.fullname)
                                    .filter(Boolean)
                                    .join(', '),
                        },
                        { title: shortLabel(FIELD_LABELS.groups), textAlign: 'right', content: (_op, i) => formatFloat(summaries[i]?.groups ?? 0) },
                        { title: shortLabel(FIELD_LABELS.kilos), textAlign: 'right', content: (_op, i) => formatFloat(summaries[i]?.kilos ?? 0) },
                        // Graded against this parent's lines only — see the
                        // scope filter in the grades memo.
                        { title: shortLabel(COLUMN_LABELS.performance), content: (op) => <PerformanceFlagChips grade={grades.get(op.id)} /> },
                    ]}
                    data={docs}
                />
                <SUpsertOrderProductionDialog
                    open={upsertOpen}
                    setOpen={setUpsertOpen}
                    orderProductionId={orderProductionId}
                />
                <SDeleteOrderProductionDialog
                    open={deleteOpen}
                    setOpen={setDeleteOpen}
                    orderProductionId={orderProductionId}
                />
            </>
        );
    }

    return (
        <DBlock>
            <CCardList<ProductionDoc>
                title={'Órdenes de producción'}
                data={docs}
                loading={loading}
                keyOf={(op) => op.id}
                renderItem={(op) => (
                    <OrderProductionCard
                        orderProduction={op}
                        grade={grades.get(op.id)}
                        onEdit={handleEditOrderProduction}
                        onDelete={handleDeleteOrderProduction}
                        onGrade={handleGradeOrderProduction}
                    />
                )}
                skip={skip}
                take={take}
                count={count}
                setSkip={setSkip}
                filterValue={filter}
                onFilterChange={(value) => setFilter(value || '')}
                filterHelperText={'Tipo, planta, máquina, producto o empleado.'}
                activeFilterCount={
                    (startDate && endDate ? 1 : 0) +
                    (orderProductionTypeId != null ? 1 : 0) +
                    (branchId != null ? 1 : 0) +
                    (machineId != null ? 1 : 0)
                }
                filters={
                    <>
                        <DDateRangePicker
                            label={'Fecha'}
                            value={
                                startDate && endDate
                                    ? [dayjs(startDate), dayjs(endDate)]
                                    : undefined
                            }
                            onChange={(range: DateRange) => {
                                setStartDate(range[0].format(DATE_FORMAT));
                                setEndDate(range[1].format(DATE_FORMAT));
                            }}
                            onClear={() => {
                                setStartDate('');
                                setEndDate('');
                            }}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={orderProductionTypeId}
                            onChange={setOrderProductionTypeId}
                            itemText={'name'}
                            label={'Tipo'}
                            options={data?.getOrderProductionTypes || []}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={branchId}
                            onChange={setBranchId}
                            itemText={'name'}
                            label={'Sucursal'}
                            options={data?.getBranches || []}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={machineId}
                            onChange={setMachineId}
                            itemText={'name'}
                            label={'Máquina'}
                            options={data?.getMachines || []}
                            disableGutters
                        />
                    </>
                }
                onItemDoubleClick={handleEditOrderProduction}
                contextItems={[
                    {
                        title: shortLabel(COLUMN_LABELS.edit),
                        callback: (orderProduction) => {
                            handleEditOrderProduction(orderProduction);
                        },
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.delete),
                        callback: (orderProduction) => {
                            handleDeleteOrderProduction(orderProduction);
                        },
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.performance),
                        callback: (orderProduction) => {
                            handleGradeOrderProduction(orderProduction);
                        },
                    },
                ]}
            />
            <SUpsertOrderProductionDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                orderProductionId={orderProductionId}
            />
            <SDeleteOrderProductionDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                orderProductionId={orderProductionId}
            />
            <OrderProductionPerformanceDialog
                open={gradeOpen}
                setOpen={setGradeOpen}
                orderProductionId={gradeProductionId}
                lines={gradeLines}
            />
        </DBlock>
    );
}
